import { cn } from '@/lib/utils'
import { elementName, formatCount, type HeatmapDevice, type HeatmapElement } from './heatmapInsights'

const VISIBLE = 8

/**
 * "Top clicked" beside the capture: the ranked answer to "what do people
 * click", readable without hunting hotspots on a scaled-down screenshot.
 * Share bars are the accent tint (the table share-bar rule), not the chart
 * ramp. Picking a row selects the same element on the capture.
 */
export function HeatmapElementList({
  elements,
  device,
  selectedKey,
  onSelect,
  onHover,
}: {
  elements: HeatmapElement[]
  device: HeatmapDevice
  selectedKey: string | null
  onSelect: (key: string | null) => void
  /** Spotlights the row's element on the capture. */
  onHover?: (key: string | null) => void
}) {
  if (elements.length === 0) return null
  const unit = device === 'mobile' ? 'taps' : 'clicks'
  const shown = elements.slice(0, VISIBLE)
  const rest = elements.slice(VISIBLE)
  const max = shown[0]?.share || 1
  const restShare = rest.reduce((sum, el) => sum + el.share, 0)

  return (
    <section
      aria-labelledby="heatmap-top-clicked"
      className="surface-raised flex w-full flex-col rounded-xl bg-card lg:sticky lg:top-4"
    >
      <div className="flex items-baseline justify-between gap-3 border-b border-border px-4 pb-3 pt-3.5">
        <h2 id="heatmap-top-clicked" className="text-sm font-semibold text-ui-title">
          {device === 'mobile' ? 'Most tapped' : 'Most clicked'}
        </h2>
        <span className="text-xs text-muted-foreground">Share of {unit}</span>
      </div>
      <ol className="flex flex-col py-1">
        {shown.map((el) => {
          const name = elementName(el) ?? 'Unlabelled element'
          const selected = el.key === selectedKey
          return (
            <li key={el.key}>
              <button
                type="button"
                onClick={() => onSelect(selected ? null : el.key)}
                onMouseEnter={() => onHover?.(el.key)}
                onMouseLeave={() => onHover?.(null)}
                onFocus={() => onHover?.(el.key)}
                onBlur={() => onHover?.(null)}
                aria-pressed={selected}
                className={cn(
                  'flex w-full flex-col gap-1.5 px-4 py-2 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring',
                  selected && 'bg-muted/60',
                )}
              >
                <span className="flex items-baseline gap-2">
                  <span className="flex size-4 shrink-0 items-center justify-center self-center rounded-full bg-neutral-950 text-[10px] font-semibold tabular-nums text-white dark:bg-white dark:text-neutral-950">
                    {el.rank}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13px] text-foreground" title={name}>
                    {name}
                  </span>
                  <span className="shrink-0 text-[13px] font-medium tabular-nums text-foreground">
                    {Math.round(el.share)}%
                  </span>
                </span>
                <span className="flex items-center gap-2 pl-6">
                  <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <span
                      className="block h-full rounded-full bg-primary-tint-ring"
                      style={{ width: `${Math.max(2, (el.share / max) * 100)}%` }}
                    />
                  </span>
                  <span className="w-12 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {formatCount(el.clicks)}
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ol>
      {rest.length > 0 && (
        <p className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
          {rest.length} more, {Math.round(restShare)}% of {unit} together
        </p>
      )}
    </section>
  )
}
